import {
    Modal,
    Button,
    ModalBody,
    ModalHeader,
    ModalFooter,
    useDisclosure,
    ModalContent,
} from "@heroui/react";
import { useState, ReactNode, useCallback } from "react";
import ConfirmContext, { ConfirmOptions } from "./ConfirmContext";
import { useTranslation } from "react-i18next";

const defaultOptions: ConfirmOptions = {
    title: "Confirm",
    description: "Are you sure you want to confirm this action?",
    dismissable: true,
    confirmText: "Confirm",
    cancelText: "Cancel",
};

const ConfirmProvider = ({ children }: { children: ReactNode }) => {
    const { isOpen, onOpen, onOpenChange, onClose } = useDisclosure();

    const [options, setOptions] = useState<ConfirmOptions | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const { t } = useTranslation("common");

    const showConfirm = useCallback(
        (options: ConfirmOptions) => {
            setOptions({ ...defaultOptions, ...options });
            onOpen();
        },
        [onOpen],
    );

    const handleConfirm = useCallback(async () => {
        if (options?.onConfirm) {
            setIsLoading(true);
            try {
                await options.onConfirm();
                setIsLoading(false);
            } catch {
                setIsLoading(false);
            }
        }
        onClose();
    }, [onClose, options]);

    const handleCancel = useCallback(async () => {
        if (options?.onCancel) {
            setIsLoading(true);
            try {
                await options.onCancel();
                setIsLoading(false);
            } catch {
                setIsLoading(false);
            }
        }
        onClose();
    }, [onClose, options]);

    return (
        <ConfirmContext.Provider value={{ showConfirm }}>
            {children}
            {isOpen && (
                <Modal
                    isOpen={isOpen}
                    onClose={handleCancel}
                    closeButton
                    onOpenChange={onOpenChange}
                    placement="center"
                >
                    <ModalContent>
                        <ModalHeader>{options?.title}</ModalHeader>
                        <ModalBody>{options?.description}</ModalBody>
                        <ModalFooter>
                            <Button
                                disabled={isLoading}
                                isLoading={isLoading}
                                onPress={handleCancel}
                            >
                                {t("cancel")}
                            </Button>
                            <Button
                                disabled={isLoading}
                                isLoading={isLoading}
                                onPress={handleConfirm}
                                color={options?.color}
                            >
                                {t("confirm")}
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
            )}
        </ConfirmContext.Provider>
    );
};

export default ConfirmProvider;
